/**
 * Microsoft Clarity loader — privacy-respecting, customer-only.
 *
 * Loads the Clarity script (heatmaps + session replays) ONLY when ALL of
 * these are true:
 *
 *   1. The Clarity project ID is configured via REACT_APP_CLARITY_ID.
 *   2. The visitor has explicitly chosen "Accept all" on the cookie
 *      consent banner. ("Essential only" → no analytics, full stop.)
 *   3. The current URL is NOT under /admin (we don't pay for replays
 *      of ourselves clicking around the admin CMS).
 *   4. The signed-in user (if any) is NOT internal — admin / staff /
 *      owner roles are excluded so internal traffic doesn't pollute the
 *      heatmaps.
 *
 * Idempotent — even if React re-renders or the user crosses a route
 * boundary that re-runs the check, we only inject the script tag once
 * per page load.
 *
 * To revoke: a user who later switches to "Essential only" stops
 * collecting from that moment forward (we listen for the storage event
 * the consent banner fires).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCookieConsent } from '../components/CookieConsent';

const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

const INTERNAL_ROLES = new Set(['admin', 'staff', 'owner']);

// Module-scoped so we never inject the script twice in the same SPA session.
let injected = false;

const injectClarity = (id) => {
  if (injected || !id) return;
  injected = true;
  // Microsoft's official snippet, adapted to a runtime call so we can
  // gate it. Functionally identical to pasting their HTML <script> tag.
  /* eslint-disable */
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i + '?ref=bwt';
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', id);
  /* eslint-enable */
};

export default function useClarity() {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!CLARITY_ID) return;            // env var not set → analytics disabled
    if (loading) return;                // wait until auth state resolves
    if (getCookieConsent() !== 'accepted') return;  // GDPR — analytics requires explicit opt-in
    if (location.pathname.startsWith('/admin')) return;
    if (user && INTERNAL_ROLES.has(user.role)) return;

    injectClarity(CLARITY_ID);
  }, [user, loading, location.pathname]);

  // Re-run the gate if cookie consent changes mid-session (banner re-opened
  // via footer "Cookie settings" and the user flips from essential → accept).
  useEffect(() => {
    const handler = () => {
      if (
        CLARITY_ID
        && getCookieConsent() === 'accepted'
        && !location.pathname.startsWith('/admin')
        && !(user && INTERNAL_ROLES.has(user.role))
      ) {
        injectClarity(CLARITY_ID);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [user, location.pathname]);
}
